import { RefObject, useEffect, useRef } from "react";

import FullCalendar from "@fullcalendar/react";

/**
 * FullCalendar measures its container once and writes the resulting pixel width/height onto its
 * internal scroll-grid tables. Those measurements are only recalculated on a window resize (or an
 * explicit `updateSize()` call), so layout changes that resize the container without resizing the
 * window — opening/closing the DropZone, collapsing the navigation — leave stale column widths
 * behind (days cut off when the container shrinks, blank space when it grows).
 *
 * Observing the container element and calling `updateSize()` on every size change keeps the grid in
 * sync. Returns the ref to attach to the element wrapping the calendar.
 */
export function useCalendarAutoSize(calendarRef: RefObject<FullCalendar | null>) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let lastWidth = container.clientWidth;
    let lastHeight = container.clientHeight;
    let frameId = 0;

    const resizeObserver = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = container;
      if (clientWidth === lastWidth && clientHeight === lastHeight) {
        return;
      }
      lastWidth = clientWidth;
      lastHeight = clientHeight;

      // Deferred to the next frame: updateSize() flushes a synchronous re-render, which must not
      // happen inside the ResizeObserver callback itself.
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => calendarRef.current?.getApi().updateSize());
    });

    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [calendarRef]);

  return containerRef;
}
